/**
 * DOM Visibility Utilities
 *
 * Helper functions for managing element visibility using data-hidden attributes
 */

/**
 * Shows an element by removing or setting data-hidden attribute
 * @param element - The HTML element to show
 * @param useFalseValue - If true, sets data-hidden="false" instead of removing the attribute
 */
export const show = (element: HTMLElement | null, useFalseValue = false): void => {
  if (!element) return
  if (useFalseValue) {
    element.setAttribute('data-hidden', 'false')
  } else {
    element.removeAttribute('data-hidden')
  }
}

/**
 * Hides an element by setting data-hidden="true"
 * @param element - The HTML element to hide
 */
export const hide = (element: HTMLElement | null): void => {
  if (!element) return
  element.setAttribute('data-hidden', 'true')
}

/**
 * Toggles element visibility based on a boolean condition
 * @param element - The HTML element to toggle
 * @param shouldShow - If true, shows the element; if false, hides it
 * @param useFalseValue - If true, uses data-hidden="false" when showing
 */
export const toggle = (element: HTMLElement | null, shouldShow: boolean, useFalseValue = false): void => {
  if (shouldShow) {
    show(element, useFalseValue)
  } else {
    hide(element)
  }
}

/**
 * Checks if an element is hidden
 * @param element - The HTML element to check
 * @returns true if the element is hidden or null, false otherwise
 */
export const isHidden = (element: HTMLElement | null): boolean => {
  if (!element) return true
  const dataHidden = element.getAttribute('data-hidden')
  return dataHidden === 'true'
}
